import { AfterViewInit, Directive, inject, Input, OnChanges, OnDestroy } from '@angular/core';
import { ArrayFunctions, ObjectFunctions } from '@campus/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TableDataMapper } from '../data/data.mapper';
import { TableComponent } from '../table.component';
import {
  GroupedData,
  TableColumnConfigInterface,
  TableFilterInterface,
  TablePaginationInterface,
  TableSortInterface,
} from '../table.interface';
import { RowFilter } from './filter';
import { RowGrouper } from './grouper';
import { RowSorter } from './sorter';

@Directive({
  standalone: false,
  selector: '[campusLocalHandling], [localHandling], [local-handling]',
})
export class Table2LocalHandlingDirective<T = any> implements AfterViewInit, OnChanges, OnDestroy {
  private table = inject(TableComponent, { host: true });

  private _searchTerm: string;
  private _visibleColumns: TableColumnConfigInterface<T>[];
  private _pagination: TablePaginationInterface;
  private _filters: TableFilterInterface<T>[];
  private _sortBy: TableSortInterface<T>;
  private _selectedIds: number[];
  private _showOnlySelection: boolean;
  private _data: T[];

  private _columnConfigDict: Record<string, TableColumnConfigInterface<T>> = {};
  private _destroy$ = new Subject<void>();

  @Input() data: T[] = [];
  @Input() searchTerm: string;
  @Input() filters: TableFilterInterface<T>[];
  @Input() pagination: TablePaginationInterface;
  @Input() columns: TableColumnConfigInterface<T>[];
  @Input() sortBy: TableSortInterface<T>;
  @Input() idKey = 'id';
  @Input() updated: unknown;
  @Input() initialSelection = [];
  @Input() groupBy: string;

  ngOnDestroy(): void {
    this._destroy$.next();
  }

  ngAfterViewInit(): void {
    this.listenToTableEvents();
  }

  ngOnChanges(changes) {
    let shouldUpdateDataSource = false;

    const hasChanges = (key: string) => {
      return changes[key] && !ObjectFunctions.deepEquals(changes[key].currentValue, changes[key].previousValue);
    };

    if (this.data && hasChanges('data')) {
      this._data = this.data;
      shouldUpdateDataSource = true;
    }

    if (this.filters && hasChanges('filters')) {
      this._filters = this.filters;
      shouldUpdateDataSource = true;
    }

    if (this.columns && hasChanges('columns')) {
      this._visibleColumns = this.columns.filter((column) => column.visible);
      this._setColumnConfigDict();
      shouldUpdateDataSource = true;
    }

    if (this.pagination && hasChanges('pagination')) {
      this._pagination = this.pagination;

      if (
        changes.pagination.currentValue &&
        changes.pagination.currentValue.from !== changes.pagination.previousValue?.from
      ) {
        shouldUpdateDataSource = true;
      }
    }

    if (this.sortBy && hasChanges('sortBy')) {
      this._sortBy = this.sortBy;
      shouldUpdateDataSource = true;
    }

    if (this.updated && changes.updated && !changes.updated.firstChange) {
      shouldUpdateDataSource = true;
    }

    if (hasChanges('searchTerm')) {
      this._searchTerm = this.searchTerm;
      shouldUpdateDataSource = true;
    }

    if (this.initialSelection && hasChanges('initialSelection')) {
      this._selectedIds = ArrayFunctions.wrapIntoArray(this.initialSelection).map((s) => s[this.idKey]);
    }

    if (shouldUpdateDataSource) this._updateDataSource();
  }

  private listenToTableEvents() {
    if (!this.table) return;

    this.table.columnsChange?.pipe(takeUntil(this._destroy$)).subscribe((columns) => {
      this._setColumnConfigDict();
      this._updateDataSource();
    });
    this.table.visibleColumnsChange?.pipe(takeUntil(this._destroy$)).subscribe((visibleColumns) => {
      this._visibleColumns = visibleColumns.selected;
      this._updateDataSource();
    });
    this.table.paginationChange?.pipe(takeUntil(this._destroy$)).subscribe((pagination) => {
      this._pagination = pagination;
      this._updateDataSource();
    });
    this.table.filtersChange?.pipe(takeUntil(this._destroy$)).subscribe((filters) => {
      this._filters = filters;
      this._updateDataSource();
    });
    this.table.sortChange?.pipe(takeUntil(this._destroy$)).subscribe((sortBy) => {
      this._sortBy = sortBy;
      this._updateDataSource();
    });
    this.table.selectionChange?.pipe(takeUntil(this._destroy$)).subscribe((selection) => {
      this._selectedIds = ArrayFunctions.wrapIntoArray(selection.selected).map((s) => s[this.idKey]);
    });
    this.table.searchTermChange?.pipe(takeUntil(this._destroy$)).subscribe((searchTerm) => {
      this._searchTerm = searchTerm;
      this._updateDataSource();
    });
    this.table.showOnlySelectionChange?.pipe(takeUntil(this._destroy$)).subscribe(({ showOnlySelection }) => {
      this._showOnlySelection = showOnlySelection;
      this._updateDataSource();
    });
  }

  private _setColumnConfigDict(): void {
    this._columnConfigDict = this.columns.reduce((acc, column) => {
      acc[column.key as string] = column;
      return acc;
    }, {} as Record<string, TableColumnConfigInterface<T>>);
  }

  private _updateDataSource() {
    let data: GroupedData<T>[] | T[] =
      this._showOnlySelection && this._selectedIds?.length
        ? (() => {
            const selectedIds = new Set(this._selectedIds);
            return this._data.filter((item) => selectedIds.has(item[this.idKey]));
          })()
        : this._data;

    if (!data) {
      this.table.dataSource = [];
      return;
    }

    if (!this._showOnlySelection) {
      data = this.__filter(data);
    }

    this.table.totalItems = data.length;
    this.table.pagination = this.table['_getPagination'](this.table.page, this.table.totalItems);

    data = this.__mapData(data);
    data = this.__sortBy(data as T[], this._sortBy?.key, this._sortBy?.direction);

    const allData = data;
    data = this.__pageData(data, this._pagination, this.groupBy);

    data = this.__groupBy(data, allData);

    this.table.dataSource = data;
  }

  private __filter(data: T[]): T[] {
    const { _searchTerm, _visibleColumns, _filters } = this;
    const rowFilter = new RowFilter(this._columnConfigDict);

    return data.filter((row) => {
      let result = true;

      if (_searchTerm) {
        result = Object.entries(row)
          .filter(([key, _]) => _visibleColumns.some((visibleColumn) => visibleColumn.key === key))
          .some(([_, value]) => RowFilter.comparers.text.contains(value, _searchTerm));
      }

      if (result && _filters && _filters.length > 0) {
        result = _filters.every(({ key, value }) => {
          return rowFilter.filter(row, key.toString(), value);
        });
      }

      return result;
    });
  }

  private __groupBy(data: T[], allData: T[]): GroupedData<T>[] | T[] {
    if (!this.groupBy) return data;

    const rowGrouper = new RowGrouper<T>(this._columnConfigDict);
    return rowGrouper.group(this.groupBy, data as T[], allData as T[], { locale: this.table.locale });
  }

  private __sortBy(data: T[], key: keyof T, direction: 'asc' | 'desc' | undefined): T[] {
    if (!key || direction === undefined) return data;

    const rowSorter = new RowSorter<T>(this._columnConfigDict);
    return rowSorter.sort(data, key, direction);
  }

  private __pageData(data: T[], pagination: TablePaginationInterface, groupBy: string): T[] {
    if (!data.length) return data;
    if (!this._pagination) return data;

    if (groupBy && this._columnConfigDict[groupBy]) {
      const rowSorter = new RowSorter<T>(this._columnConfigDict);
      data = rowSorter.sort(
        data,
        this._columnConfigDict[groupBy].key,
        this._sortBy?.key === this.groupBy ? this._sortBy.direction || 'asc' : 'asc'
      );
    }

    return data.slice(Math.max(pagination.from - 1, 0), pagination.to);
  }

  private __mapData(data: T[]): T[] {
    const tableDataMapper = new TableDataMapper<T>(this._columnConfigDict);
    return tableDataMapper.map(data);
  }
}
